import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "../lib/api";

// Uses backend proxy /api/geocode/search which merges:
//  - US Census Geocoder (great for US street addresses, no CORS)
//  - Nominatim / OpenStreetMap (global fallback + POI)
const inputCls =
  "w-full rounded-lg border border-slate-300 pl-9 pr-10 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

// --- component ------------------------------------------------------------
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  testId = "address-autocomplete",
  countrycodes = "us",
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const boxRef = useRef(null);
  const skipNextFetch = useRef(false);
  const seqRef = useRef(0);

  useEffect(() => setQuery(value || ""), [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    if (!query || query.trim().length < 3) { setSuggestions([]); return; }
    const mySeq = ++seqRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q: query });
        if (countrycodes) params.set("country", countrycodes);
        const data = await api.get(`/geocode/search?${params.toString()}`);
        if (mySeq !== seqRef.current) return; // stale
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlight(-1);
      } catch (_) { /* ignore */ }
      finally { if (mySeq === seqRef.current) setLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, countrycodes]);

  const pick = (s) => {
    skipNextFetch.current = true;
    setQuery(s.address);
    setOpen(false);
    setSuggestions([]);
    onChange?.(s.address);
    onSelect?.(s);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { if (highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); } }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        data-testid={testId}
        value={query}
        placeholder={placeholder}
        onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value); }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={onKey}
        autoComplete="off"
        className={inputCls}
      />
      {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      {open && suggestions.length > 0 && (
        <ul data-testid={`${testId}-list`} className="absolute z-[60] mt-1 w-full max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {suggestions.map((s, i) => (
            <li
              key={s.key}
              data-testid={`${testId}-option-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 text-sm cursor-pointer border-b border-slate-100 last:border-b-0 ${highlight === i ? "bg-slate-100" : "hover:bg-slate-50"}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 truncate flex-1">{s.title}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${s.source === "census" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.source === "census" ? "USPS" : "OSM"}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{s.subtitle}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
